import { useNode, Element } from "@craftjs/core";
import React, { useContext, useEffect, useState } from "react";
import { SliderSettings } from "./settings";
import GrigoraCSSRenderer from "../css-render";
import icon from "./icon";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import { Container } from "../container";

import {
  Navigation,
  A11y,
  Pagination,
  Scrollbar,
  Virtual,
  Mousewheel,
  Parallax,
  FreeMode,
  Grid,
  Manipulation,
  Zoom,
  Controller,
  Autoplay,
  EffectFade,
  EffectCube,
  EffectFlip,
  EffectCoverflow,
  EffectCards,
  EffectCreative,
  Thumbs,
  Keyboard,
} from "swiper/modules";

import { Swiper, SwiperSlide } from "swiper/react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { makeID } from "../../helpers";
import { GrigoraEditorContext } from "../../editor/context";

function getImageAttachmentURL(srcMode, src, projectID) {
  if (!projectID) {
    projectID = src?.projectID;
  }
  if (srcMode === "cdn") {
    if (src?.og) {
      return `https://cdn.grigora.co/projects/${projectID}/${src?.og}`;
    }
    return `https://cdn.grigora.co/projects/${projectID}/${src?.name}.${src?.extension}`;
  } else if (srcMode === "link") {
    return src;
  } /* only in editor */ else if (srcMode === "preview") {
    return src;
  }
  return undefined;
}

export const Slider = (props) => {
  const {
    id,
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const { blockStates, setBlockState } =
    useContext(GrigoraDashboardContext) ?? {};

  var {
    totalSlides = 3,
    isClickable,
    loop,
    hasDynamicBullets,
    grabCursor,
    hasPagination = true,
    paginationType,
    spaceBetween,
    spaceBetweenTablet,
    spaceBetweenMobile,
    slidePerView,
    slidePerViewTablet,
    slidePerViewMobile,
    isCentered,
    isFreeMode,
    isInfiniteLoop,
    hasEffect,
    effectType,
    hasMouseWheel,
    hasAutoplay,
    hasParallax,
    isKeyboardChange,
    hasScrollbar,
    isVertical,
    autoplayDelay = 3000,
    disableAutoplayOnInteraction,
    parallaxBackground,
    navigation = true,
    sliderSpeed = 300,
    hasStaticSlide,
    rewind,
    cubeEffectShadow = true,
    cubeEffectSlideShadows = true,
    coverflowEffectRotate = 50,
    coverflowEffectSlideShadows = true,
    creativeEffectPrevShadow = true,
    creativeEffectNextShadow = true,
  } = props;
  const { projectID } = useContext(GrigoraDashboardContext);
  const [uniqueKey, setUniqueKey] = useState("");

  const editorContext = useContext(GrigoraEditorContext);

  var device = editorContext?.displaySize ?? "desktop";

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-slider": true,
      [`slider-${id}`]: id,
      "has-static-slide": hasStaticSlide,
    },
    blockProps.className,
  );

  useEffect(() => {
    setUniqueKey(makeID(5));
  }, [
    isClickable,
    loop,
    hasDynamicBullets,
    grabCursor,
    hasPagination,
    paginationType,
    spaceBetween,
    spaceBetweenTablet,
    spaceBetweenMobile,
    slidePerView,
    device,
    slidePerViewTablet,
    slidePerViewMobile,
    isCentered,
    isFreeMode,
    isInfiniteLoop,
    hasEffect,
    effectType,
    hasMouseWheel,
    hasAutoplay,
    hasParallax,
    isKeyboardChange,
    hasScrollbar,
    isVertical,
    sliderSpeed,
    parallaxBackground,
    autoplayDelay,
    disableAutoplayOnInteraction,
    hasStaticSlide,
    cubeEffectShadow,
    cubeEffectSlideShadows,
    coverflowEffectRotate,
    coverflowEffectSlideShadows,
    creativeEffectPrevShadow,
    creativeEffectNextShadow,
  ]);

  // TODO: Add more versions of creative effect
  let creativeEffectPrevVersions =
    effectType === "creative"
      ? {
          shadow: creativeEffectPrevShadow,
          translate: [0, 0, -400],
        }
      : effectType === "creative-v1"
      ? {
          shadow: creativeEffectPrevShadow,
          translate: ["-120%", 0, -500],
        }
      : effectType === "creative-v2"
      ? {
          shadow: creativeEffectPrevShadow,
          translate: ["-20%", 0, -1],
        }
      : effectType === "creative-v3"
      ? {
          shadow: creativeEffectPrevShadow,
          translate: [0, 0, -800],
          rotate: [180, 0, 0],
        }
      : effectType === "creative-v4"
      ? {
          shadow: creativeEffectPrevShadow,
          translate: ["-125%", 0, -800],
          rotate: [0, 0, -90],
        }
      : effectType === "creative-v5"
      ? {
          shadow: creativeEffectPrevShadow,
          origin: "left center",
          translate: ["-5%", 0, -200],
          rotate: [0, 100, 0],
        }
      : undefined;

  let creativeEffectNextVersions =
    effectType === "creative"
      ? {
          shadow: creativeEffectNextShadow,
          translate: ["100%", 0, 0],
        }
      : effectType === "creative-v1"
      ? {
          shadow: creativeEffectNextShadow,
          translate: ["120%", 0, -500],
        }
      : effectType === "creative-v2"
      ? {
          shadow: creativeEffectNextShadow,
          translate: ["100%", 0, 0],
        }
      : effectType === "creative-v3"
      ? {
          shadow: creativeEffectNextShadow,
          translate: [0, 0, -800],
          rotate: [-180, 0, 0],
        }
      : effectType === "creative-v4"
      ? {
          shadow: creativeEffectNextShadow,
          translate: ["125%", 0, -800],
          rotate: [0, 0, 90],
        }
      : effectType === "creative-v5"
      ? {
          origin: "right center",
          translate: ["5%", 0, -200],
          rotate: [0, -100, 0],
        }
      : undefined;

  return (
    <div
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      onClick={() => selected}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Slider(id, props)}`}</style>

      <Swiper
        key={`swiper-${uniqueKey}`}
        slidesPerView={
          effectType === "coverflow"
            ? "auto"
            : device === "desktop"
            ? slidePerView
            : device === "tablet"
            ? slidePerViewTablet
            : device === "mobile"
            ? slidePerViewMobile
            : slidePerView
        }
        spaceBetween={
          device === "desktop"
            ? spaceBetween
            : device === "tablet"
            ? spaceBetweenTablet
            : device === "mobile"
            ? spaceBetweenMobile
            : spaceBetween
        }
        className={hasStaticSlide ? "has-static-slide" : undefined}
        freeMode={isFreeMode}
        rewind={rewind}
        scrollbar={
          hasScrollbar
            ? {
                hide: true,
              }
            : undefined
        }
        direction={
          isVertical ||
          (paginationType === "pagination-default" &&
            !paginationType &&
            !navigation)
            ? "vertical"
            : "horizontal"
        }
        speed={sliderSpeed}
        parallax={hasParallax}
        creativeEffect={
          effectType === "creative"
            ? {
                prev: {
                  shadow: creativeEffectPrevShadow,
                  translate: [0, 0, -400],
                },
                next: {
                  shadow: creativeEffectNextShadow,
                  translate: ["100%", 0, 0],
                },
              }
            : undefined
        }
        grabCursor={grabCursor}
        centeredSlides={isCentered}
        coverflowEffect={
          effectType === "coverflow"
            ? {
                rotate: coverflowEffectRotate,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: coverflowEffectSlideShadows,
              }
            : undefined
        }
        cubeEffect={
          effectType === "cube"
            ? {
                shadow: cubeEffectShadow,
                slideShadows: cubeEffectSlideShadows,
                shadowOffset: 20,
                shadowScale: 0.94,
              }
            : undefined
        }
        onSwiper={(swiper) => setBlockState(id, `swiper`, swiper)}
        effect={
          effectType === "fade"
            ? "fade"
            : effectType === "cube"
            ? "cube"
            : effectType === "flip"
            ? "flip"
            : effectType === "cards"
            ? "cards"
            : effectType === "coverflow"
            ? "coverflow"
            : effectType === "creative"
            ? "creative"
            : undefined
        }
        keyboard={{
          enabled: isKeyboardChange,
        }}
        pagination={
          hasPagination
            ? {
                clickable: isClickable,
                type:
                  paginationType === "pagination-progress"
                    ? "progressbar"
                    : paginationType === "pagination-fraction"
                    ? "fraction"
                    : isVertical ||
                      paginationType === "pagination-default" ||
                      !paginationType
                    ? "bullets"
                    : undefined,
                dynamicBullets: hasDynamicBullets,
              }
            : undefined
        }
        mousewheel={hasMouseWheel}
        allowTouchMove={false}
        navigation={isVertical ? false : navigation}
        autoplay={
          hasAutoplay
            ? {
                delay: autoplayDelay,
                disableOnInteraction: disableAutoplayOnInteraction,
              }
            : undefined
        }
        loop={loop}
        modules={[
          Navigation,
          A11y,
          Pagination,
          Scrollbar,
          Virtual,
          Mousewheel,
          Parallax,
          FreeMode,
          Grid,
          Manipulation,
          Zoom,
          Controller,
          Autoplay,
          EffectFade,
          EffectCube,
          EffectFlip,
          EffectCoverflow,
          EffectCards,
          EffectCreative,
          Thumbs,
          Keyboard,
        ]}
      >
        {hasStaticSlide && (
          <>
            <Element
              width={"100%"}
              height={"100%"}
              padding={{
                top: "0px",
                bottom: "0px",
                left: "0px",
                right: "0px",
              }}
              id={`slide-${id}-static`}
              canvas
              is={Container}
            ></Element>
          </>
        )}
        {hasParallax && (
          <div
            slot="container-start"
            className="parallax-bg"
            style={{
              backgroundImage: `url(
            ${getImageAttachmentURL(
              parallaxBackground?.srcMode,
              parallaxBackground?.src,
              projectID,
            )}
              )`,
            }}
            data-swiper-parallax="-23%"
          ></div>
        )}
        {Array.from({ length: totalSlides }).map((slide, i) => {
          return (
            <SwiperSlide key={`slide-${id}-${i}-key`}>
              <Element
                custom={{
                  displayName: `Slide ${i + 1}`,
                }}
                width={"100%"}
                height={"100%"}
                padding={{
                  top: "0px",
                  bottom: "0px",
                  left: "0px",
                  right: "0px",
                }}
                id={`slide-${id}-${i}`}
                canvas
                is={Container}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

// To Avoid Mangling in Production
Slider.displayName = "Slider";

Slider.craft = {
  name: "Slider",
  props: {},
  related: {
    settings: SliderSettings,
    icon: icon,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "miscellaneous",
    keywords: ["slider", "slider"],
  },
  custom: {
    name: "Slider",
    slug: "grigora/slider",
  },
};
